import { Link } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, Shield, Eye, Lock, Database, Mail, Cookie } from "lucide-react";

export default function PrivacyPolicy({ user, logout }) {
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="privacy-title">
                Política de Privacidad
              </h1>
            </div>
            <p className="text-muted-foreground">
              Última actualización: Enero 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Intro */}
            <section className="bg-gray-50 rounded-2xl p-6 border" data-testid="privacy-intro">
              <p className="text-lg leading-relaxed">
                En <strong>BulkBytes</strong> nos tomamos muy en serio la privacidad de nuestros usuarios. 
                Esta política describe cómo recopilamos, usamos y protegemos tu información personal cuando 
                utilizas nuestra plataforma de compras grupales.
              </p>
            </section>

            {/* Section 1 */}
            <section data-testid="privacy-section-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Database className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  1. Información que Recopilamos
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Información de cuenta:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Nombre y apellidos</li>
                    <li>Dirección de correo electrónico</li>
                    <li>Foto de perfil (si utilizas Google para iniciar sesión)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium mb-2">Información de transacciones:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Historial de participaciones en ofertas grupales</li>
                    <li>Datos de pago procesados de forma segura</li>
                    <li>Dirección de envío para entregas confirmadas</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium mb-2">Información técnica:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Dirección IP y tipo de navegador</li>
                    <li>Datos de uso y navegación en la plataforma</li>
                    <li>Información del dispositivo</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section data-testid="privacy-section-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  2. Cómo Utilizamos tu Información
                </h2>
              </div>
              <div className="ml-13">
                <p className="text-muted-foreground mb-4">
                  Utilizamos la información recopilada para:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Gestionar tu cuenta y participaciones en ofertas grupales</li>
                  <li>Procesar pagos y reembolsos de forma segura</li>
                  <li>Enviarte notificaciones sobre el estado de tus compras</li>
                  <li>Coordinar la entrega de productos confirmados</li>
                  <li>Mejorar nuestros servicios y experiencia de usuario</li>
                  <li>Prevenir fraudes y actividades no autorizadas</li>
                  <li>Cumplir con obligaciones legales y fiscales</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section data-testid="privacy-section-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  3. Protección de tus Datos
                </h2>
              </div>
              <div className="ml-13">
                <p className="text-muted-foreground mb-4">
                  Implementamos medidas de seguridad técnicas y organizativas para proteger tu información:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Cifrado SSL/TLS en todas las comunicaciones</li>
                  <li>Los datos de pago son procesados directamente por nuestro proveedor de pagos (nunca almacenamos números de tarjeta)</li>
                  <li>Acceso restringido a datos personales solo al personal autorizado</li>
                  <li>Copias de seguridad cifradas y almacenadas de forma segura</li>
                  <li>Monitorización continua de actividades sospechosas</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section data-testid="privacy-section-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Cookie className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  4. Cookies y Tecnologías Similares
                </h2>
              </div>
              <div className="ml-13">
                <p className="text-muted-foreground mb-4">
                  Utilizamos cookies esenciales para:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Mantener tu sesión iniciada de forma segura</li>
                  <li>Recordar tus preferencias de usuario</li>
                  <li>Analizar el uso de la plataforma para mejorar el servicio</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Puedes configurar tu navegador para bloquear cookies, aunque esto puede afectar la funcionalidad del sitio.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section data-testid="privacy-section-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  5. Tus Derechos
                </h2>
              </div>
              <div className="ml-13">
                <p className="text-muted-foreground mb-4">
                  De acuerdo con el RGPD y la LOPDGDD, tienes derecho a:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Acceso:</strong> Solicitar una copia de tus datos personales</li>
                  <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
                  <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos</li>
                  <li><strong>Portabilidad:</strong> Recibir tus datos en formato estructurado</li>
                  <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos</li>
                  <li><strong>Limitación:</strong> Restringir el uso de tus datos en ciertas circunstancias</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Para ejercer estos derechos, contacta con nosotros en <strong>bulkbytes26@gmail.com</strong>
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section data-testid="privacy-section-6">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                6. Terceros y Transferencias
              </h2>
              <p className="text-muted-foreground mb-4">
                Compartimos datos únicamente con:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Proveedor de pagos:</strong> Para procesar pagos de forma segura</li>
                <li><strong>Google:</strong> Si utilizas el inicio de sesión con Google</li>
                <li><strong>Empresas de logística:</strong> Para la entrega de productos confirmados</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                No vendemos ni alquilamos tus datos personales a terceros.
              </p>
            </section>

            {/* Section 7 */}
            <section data-testid="privacy-section-7">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                7. Conservación de Datos
              </h2>
              <p className="text-muted-foreground">
                Conservamos tus datos mientras mantengas tu cuenta activa y durante el tiempo necesario para 
                cumplir con obligaciones legales (generalmente 5 años para datos fiscales). Puedes solicitar 
                la eliminación de tu cuenta en cualquier momento.
              </p>
            </section>

            {/* Section 8 */}
            <section data-testid="privacy-section-8">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                8. Cambios en esta Política
              </h2>
              <p className="text-muted-foreground">
                Podemos actualizar esta política periódicamente. Te notificaremos de cambios significativos 
                por email o mediante un aviso destacado en la plataforma. Te recomendamos revisar esta 
                página regularmente.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 rounded-2xl p-6 border border-primary/20" data-testid="privacy-contact">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Contacto
              </h2>
              <p className="text-muted-foreground">
                Si tienes preguntas sobre esta política de privacidad o sobre cómo tratamos tus datos, 
                puedes contactarnos en:
              </p>
              <div className="mt-4 space-y-2">
                <p><strong>Email:</strong> bulkbytes26@gmail.com</p>
                <p><strong>Responsable del tratamiento:</strong> BulkBytes S.L.</p>
                <p><strong>CIF:</strong> B22503882</p>
                <p><strong>Ubicación:</strong> Tarragona, España</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2025 BulkBytes. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
